import yup from 'yup';
import { requiredEmailType, requiredAutocompleteType } from '@buyside/util/validationTypes.js';
import { InvestorEmploymentTypes } from '@interfaces/userApplication/userApplication';

const { employed, seekingEmployment, student } = InvestorEmploymentTypes;

export const informationSchema = yup.object().shape({
  firstName: yup
    .string()
    .ensure()
    .required('This field is required.')
    .min(2, 'Must be at least 2 characters.')
    .max(255, 'This text is too long.'),
  lastName: yup
    .string()
    .ensure()
    .required('This field is required.')
    .min(2, 'Must be at least 2 characters.')
    .max(255, 'This text is too long.'),
  email: requiredEmailType,
  linkedinUrl: yup
    .string()
    .nullable()
    .matches(/(http(s))?(:\/\/)?([\w]+\.)?linkedin\.com\/in\/[A-z0-9_-]+\/?/, {
      message: 'Invalid LinkedIn Profile URL.',
      excludeEmptyString: true
    }),
  city: requiredAutocompleteType
});

export const applicantTypePageSchema = yup.object().shape({
  applicantType: yup
    .string()
    .ensure()
    .required('This field is required.')
});

const genericRequired = yup
  .string()
  .ensure()
  .required('This field is required.');

const genericNotRequired = yup
  .string()
  .ensure()
  .notRequired();

const genericEmploymentRequired = {
  is: (value: InvestorEmploymentTypes): boolean => [employed, seekingEmployment].includes(value),
  then: genericRequired,
  otherwise: genericNotRequired
};

const genericSchoolRequired = {
  is: student,
  then: genericRequired,
  otherwise: genericNotRequired
};

export const proEmploymentStatusSchema = yup.object().shape({
  employmentStatus: genericRequired,
  employerName: yup.string().when('employmentStatus', {
    is: (value: InvestorEmploymentTypes): boolean => [employed, seekingEmployment].includes(value),
    then: yup
      .string()
      .ensure()
      .required('This field is required.')
      .min(3, 'Must be at least 3 characters.')
      .max(255, 'This text is too long.'),
    otherwise: genericNotRequired
  }),
  title: yup.string().when('employmentStatus', genericEmploymentRequired),
  employerFundType: yup.string().when('employmentStatus', genericEmploymentRequired),
  employerAumRange: yup.string().when('employmentStatus', genericEmploymentRequired),
  investorType: yup.string().when('employmentStatus', {
    is: employed,
    then: genericRequired,
    otherwise: genericNotRequired
  }),
  schoolName: yup.string().when('employmentStatus', {
    is: student,
    then: yup
      .string()
      .ensure()
      .required('Please select from the result list.'),
    otherwise: genericNotRequired
  }),
  degree: yup.string().when('employmentStatus', genericSchoolRequired),
  fieldOfStudy: yup.string().when('employmentStatus', genericSchoolRequired),
  yearStart: yup.string().when('employmentStatus', genericSchoolRequired),
  expectedGraduation: yup.string().when('employmentStatus', genericSchoolRequired)
});

export const basicInvestorTypePageSchema = yup.object().shape({
  basicInvestorType: requiredAutocompleteType,
  basicEmployerName: genericNotRequired
});

export const applicationSubmissionSchema = yup.object().shape({
  acceptedDisclaimer: yup.boolean().oneOf([true], 'Please accept this disclaimer.')
});
